import React from "react";
import { Link } from "react-router-dom";
import MenuBar from "../components/MenuBar";
import FooterSection from "../components/FooterSection";
import PageSEO from "../components/PageSEO";
import "../styles/CROService.css";

export default function CROPage() {
  return (
    <div className="cro-page">
      <PageSEO
        title="CRO Services | Conversion Rate Optimization Agency | Sparkdenz"
        description="Increase conversion rates with Sparkdenz CRO services including A/B testing, landing page optimization, funnel analysis, and mobile UX improvements."
        canonicalPath="/cro"
      />
      <MenuBar />
      <main>
        {/* Hero Section */}
        <section className="service-hero">
          <div className="section-inner">
            <p className="service-tag">Conversion Rate Optimization</p>
            <h1>Turn More Visitors Into Customers</h1>
            <p className="service-hero-text">
              Get more revenue from your existing traffic. Our data-driven CRO methodology 
              identifies friction points, tests solutions, and optimizes every part of your 
              user experience to increase conversions.
            </p>
            <div className="service-hero-actions">
              <Link className="btn primary" to="/contact-us">
                Get Free CRO Audit
              </Link>
              <Link className="btn ghost" to="/contact-us">
                Book Consultation
              </Link>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="service-details">
          <div className="section-inner">
            <h2>CRO Services</h2>
            <div className="services-grid">
              <div className="service-item">
                <div className="service-icon">📊</div>
                <h3>Conversion Audit & Analysis</h3>
                <p>
                  We conduct comprehensive audits to identify friction points, drop-off areas, 
                  and optimization opportunities throughout your customer journey. Data-driven 
                  insights reveal what's working and what's not.
                </p>
                <ul className="service-list">
                  <li>Heatmap analysis</li>
                  <li>Session recording review</li>
                  <li>Funnel analysis</li>
                  <li>User behavior mapping</li>
                  <li>Drop-off identification</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">🧪</div>
                <h3>A/B Testing & Experimentation</h3>
                <p>
                  Test don't guess. We design and run hypothesis-driven experiments that validate 
                  improvements before full implementation. Every test is statistically significant 
                  and tied to business metrics.
                </p>
                <ul className="service-list">
                  <li>A/B test design</li>
                  <li>Multivariate testing</li>
                  <li>Statistical analysis</li>
                  <li>Result documentation</li>
                  <li>Winning variant implementation</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">🎯</div>
                <h3>Landing Page Optimization</h3>
                <p>
                  Landing pages are conversion machines. We optimize headlines, copy, forms, 
                  social proof, and CTAs to eliminate friction and guide visitors toward action 
                  with maximum persuasiveness.
                </p>
                <ul className="service-list">
                  <li>Copywriting optimization</li>
                  <li>Form field optimization</li>
                  <li>CTA button testing</li>
                  <li>Value prop clarity</li>
                  <li>Social proof integration</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">🛒</div>
                <h3>E-Commerce Optimization</h3>
                <p>
                  From product page to checkout, we optimize every step of the e-commerce journey. 
                  Even small improvements in conversion rate multiply across thousands of transactions 
                  into significant revenue gains.
                </p>
                <ul className="service-list">
                  <li>Product page optimization</li>
                  <li>Checkout flow analysis</li>
                  <li>Cart abandonment fixes</li>
                  <li>Payment method optimization</li>
                  <li>Trust signal placement</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">📱</div>
                <h3>Mobile Optimization</h3>
                <p>
                  Mobile traffic is the majority. We ensure your mobile experience is frictionless, 
                  fast-loading, and conversion-focused. Mobile-specific optimization yields massive 
                  conversion gains.
                </p>
                <ul className="service-list">
                  <li>Mobile UX design</li>
                  <li>Touch-friendly interactions</li>
                  <li>Mobile form optimization</li>
                  <li>Page speed improvement</li>
                  <li>Mobile checkout flow</li>
                </ul>
              </div>

              <div className="service-item">
                <div className="service-icon">📈</div>
                <h3>Personalization & Segmentation</h3>
                <p>
                  One-size-fits-all doesn't optimize. We segment audiences and personalize experiences 
                  based on user behavior, source, device, and more to deliver the right message to 
                  the right person at the right time.
                </p>
                <ul className="service-list">
                  <li>Audience segmentation</li>
                  <li>Dynamic content</li>
                  <li>Exit-intent optimization</li>
                  <li>Device-specific targeting</li>
                  <li>Traffic source optimization</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="service-results">
          <div className="section-inner">
            <h2>Conversion Improvements</h2>
            <div className="results-grid">
              <div className="result-card">
                <div className="result-stat">47%</div>
                <p>Average conversion rate increase</p>
              </div>
              <div className="result-card">
                <div className="result-stat">$1.2M</div>
                <p>Average annual revenue impact</p>
              </div>
              <div className="result-card">
                <div className="result-stat">156%</div>
                <p>Average RPV improvement</p>
              </div>
              <div className="result-card">
                <div className="result-stat">92%</div>
                <p>Tests with positive results</p>
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="service-process">
          <div className="section-inner">
            <h2>Our CRO Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">01</div>
                <h3>Discover & Research</h3>
                <p>User testing, analytics, and behavior analysis to understand conversion barriers.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">02</div>
                <h3>Hypothesize & Prioritize</h3>
                <p>Create testable hypotheses and prioritize opportunities by impact and effort.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">03</div>
                <h3>Test & Validate</h3>
                <p>Run statistically significant tests and validate winner before implementation.</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">04</div>
                <h3>Implement & Scale</h3>
                <p>Roll out winning variants and scale improvements across your entire funnel.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="service-cta">
          <div className="section-inner">
            <h2>Convert More Visitors Today</h2>
            <p>Get a free CRO audit and discover your biggest conversion opportunities.</p>
            <div className="cta-buttons">
              <Link className="btn primary" to="/contact-us">
                Get Free Audit
              </Link>
              <Link className="btn ghost" to="/contact-us">
                Talk to CRO Expert
              </Link>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
